import { ErrorHandler, LOCALE_ID, NgModule } from '@angular/core'
import { BrowserModule } from '@angular/platform-browser'
import { Router, RouteReuseStrategy } from '@angular/router'
import { HttpClientModule } from '@angular/common/http'
import * as Sentry from '@sentry/angular'
import { TranslocoRootModule } from './transloco-root.module'
import { L10n, setCulture } from '@syncfusion/ej2-base'
import { IonicModule, IonicRouteStrategy } from '@ionic/angular'
import { AppComponent } from './app.component'
import { AppRoutingModule } from './app-routing.module'
import { FeathersService } from './services/feathers.service'
import { AuthGuardService } from './services/guards/auth-guard.service'
import { NonAuthGuardService } from './services/guards/non-auth-guard.service'
import { ToasterService } from './features/notification/toaster.service'
import { DebugService } from './services/debug/debug.service'
import { CommonModule, registerLocaleData } from '@angular/common'
import localeCs from '@angular/common/locales/cs'
import { NoopAnimationsModule } from '@angular/platform-browser/animations'
import { ProgressBarModalModule } from './features/notification/progressBarManager/progress-bar-modal/progress-bar-modal.module'
import { SuperadminConsoleComponent } from './pages/superadmin/superadmin-console/superadmin-console.component'
import { FloatoverModule } from './features/floatover/floatover.module'
import { ActiveModuleGuardService } from './services/guards/active-module-guard.service'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { ServiceWorkerModule } from '@angular/service-worker'
import { environment } from '../environments/environment'
import { GestureRefresherComponent } from './components/gesture-refresher/gesture-refresher.component'
import { ConnectionAlertService } from './services/connectionAlert/connection-alert.service'
import { GlobalErrorHandlerService } from './services/exception/global-error-handler.service'
import { ExceptionHelperService } from './services/exception/exception-helper.service'
import { NgxsModule } from '@ngxs/store'
import { APP_STATES } from './store'
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin'
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin'
import { providePrimeNG } from 'primeng/config'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import { PrimeNgCustomPreset } from './primeNgSettings/prime-ng-custom-preset'
import primeNgTranslations from './primeNgSettings/prime-ng-cs.json'

registerLocaleData(localeCs)

@NgModule({
  bootstrap: [AppComponent],
  declarations: [AppComponent, SuperadminConsoleComponent, GestureRefresherComponent],
  imports: [
    BrowserModule,
    IonicModule.forRoot({
      animated: false,
      navAnimation: null,
      innerHTMLTemplatesEnabled: true,
      mode: 'md',
    }),
    AppRoutingModule,
    CommonModule,
    ProgressBarModalModule,
    FormsModule,
    ReactiveFormsModule,
    NoopAnimationsModule,
    HttpClientModule,
    FloatoverModule,
    TranslocoRootModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    NgxsModule.forRoot(APP_STATES),
    NgxsLoggerPluginModule.forRoot(),
    NgxsReduxDevtoolsPluginModule.forRoot(),
    IonicModule,
  ],
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    FeathersService,
    ConnectionAlertService,
    AuthGuardService,
    NonAuthGuardService,
    ActiveModuleGuardService,
    ToasterService,
    DebugService,
    { provide: LOCALE_ID, useValue: 'cs' },
    ExceptionHelperService,
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: PrimeNgCustomPreset,
        options: {
          prefix: 'prime',
          darkModeSelector: false, // disable darkmode option
        },
      },
      translation: primeNgTranslations.cs,
    }),
  ],
})
export class AppModule {
  constructor(trace: Sentry.TraceService) {
    if (environment.SENTRY_ENABLED) {
      Sentry.init({
        dsn: environment.SENTRY_DSN,
        sendDefaultPii: true,
        integrations: [Sentry.browserTracingIntegration()],
        tracesSampleRate: 1.0,
      })
    }

    setCulture('cs-CZ')
    // Load locale from assets
    fetch('/assets/locale.json')
      .then((response) => response.json())
      .then((locale) => {
        L10n.load(locale)
      })
  }
}
